<template>
    <div 
        class="vod-wrapper"
        :class="{'vod-wrapper_isEmptySlider': isEmptyMostPopular}"
        tabindex="0"
        data-no-focus-scroll
        @focusin="onFocusIn"
        @focusout="onFocusout"
    >
        <most-popular 
            ref="MostPopularSlide"
            :slides="mostPopularSlides"
            @slide-up="focusToHeader"
            @slide-down="focusFirstRow"
            @on-item-selected="itemsSelected"
            @ready="readyMostPopular"
        />
        <Lines-vod 
            ref="linesVod" 
            @ready="readyLines"
            @show-all="showAllItems"
            @list-item-selected="itemsSelected"
        />
    </div>
</template>

<script lang="ts" setup>
    import { ref, type Ref, computed, type ComputedRef, onUnmounted, nextTick, watch, onMounted } from "vue"
    import type { OnDemandCardData, TabsGenresCardData, FiltersVodCardData, GenreCardData } from "@/components/Cards/types";
    import { linkItems } from "@/components/Header/types/LinkItemsCollection";
    import { Logger } from '@/utils';
    import { PlayItem } from "@ca.vmedia.tv/domains";
    import type { LinesVodRef } from "@/components/widgets/LinesVod/types";
    import type { MostPopularRef } from "@/components/widgets/MostPopular/types";
    import { useFocus } from "@/composables/useFocus";
    import  { 
        type NavigationGuardNext, 
        type RouteLocationNormalizedGeneric, 
        type RouteLocationNormalizedLoadedGeneric, 
        useRouter,
        useRoute,
        onBeforeRouteLeave,
        onBeforeRouteUpdate
    } from "vue-router";
    import { useMostPopularVod } from "@/stores/MostPopularVod";
    import { storeToRefs } from 'pinia';
    import LinesVod from "@/components/widgets/LinesVod/LinesVod.vue";
    import { useLinesVodStore } from "@/stores/LinesVod";
    import { useApplicationStore } from "@/stores/Application";
    import { useParentControlStore } from "@/stores/ParentControl";
    import { useDetailPageStore } from "@/stores/DetailPage";
    import { AppConstants } from "@/common";
    import type {IEventSelected } from "@/components/RowView/types";
    import { Strings } from '@/locales/types/enums';
    import { ScreenName } from "@/common/types/screen-names";

    const { 
        setViewedDetailItems
    } = useDetailPageStore();

    const { setIsHaveFocusedElement, focusElementLine, focusToHeader } = useFocus();

    const ParentControlStoreModule = useParentControlStore();
    const {
        getIsModalParentVisible: isTypeModeEnabledPin,
    } = storeToRefs(ParentControlStoreModule);

    const ApplicationStore = useApplicationStore();
    const {
        getFilterVod,
        getDisableRouteChange: disableRouteChange,
        getIsHaveFocusedElement: isHavSelectedIdProgram,
    } = storeToRefs(ApplicationStore);
    const {
        setDisableRouteChange,
        setFilter,
        setIsLoadingPageIndicator,
        setTooltipeShowing,
        setTooltipeMessage
    } = ApplicationStore;

    const MostPopularVodPageStore = useMostPopularVod();
    const {
        getMostPopularVod: mostPopularSlides
    } = storeToRefs(MostPopularVodPageStore);

    const { clearLinesVodStoreByFilter } = useLinesVodStore();

    definePageMeta({
        middleware: ['set-page-indicator', 'fetch-vod'],
    });

    const { t } = useI18n();
    const router = useRouter();
    const route = useRoute();
    const { $fire, $analytics, $navigator, $eventBus, } = useNuxtApp();
    const { shelfKeys }: typeof AppConstants = AppConstants;

    const traceOnDemandScreen = $fire.trace($fire.performance, 'ON_DEMAND_SCREEN');
    try {
        traceOnDemandScreen.start();
        Logger.captureLog('ON_DEMAND_SCREEN start');
    } catch (e){
        Logger.captureLog(e);
    }

    const isReadyMostPopular: Ref<boolean> = ref(false);
    const isEmptyMostPopular: Ref<boolean> = ref(false);
    const readyMostPopular = (event: {isEmptySlider: boolean}): void => {
        if (event === undefined) {
            return;
        }
        
        isEmptyMostPopular.value = event.isEmptySlider;
        isReadyMostPopular.value = true;
    };

    const isReadyLines: Ref<boolean> = ref(false);
    const linesVod: Ref<LinesVodRef | null> = ref(null);
    const MostPopularSlide: Ref<MostPopularRef | null> = ref(null);

    const isReadyAll: ComputedRef<boolean> = computed((): boolean => {
        return isReadyLines.value && isReadyMostPopular.value && linesVod.value !== null && MostPopularSlide.value !== null;
    });

    const readyLines = (): void => {
        isReadyLines.value = true;
    };
    const onFocusIn = (): void => {
        registerKeyListeners();
    };
    const onFocusout = (): void => {
        unregisterKeyListeners();
    };
    const registerKeyListeners = (): void => {
        $navigator.on("keypress:back", keyBackListener);
    };
    const unregisterKeyListeners = (): void => {
        $navigator.off("keypress:back", keyBackListener);
    };
    const keyBackListener = (): void => {
        focusToHeader();
    };
    const showAllItems = (val: IEventSelected): void => {
        switch (val.lineId) {
            case shelfKeys.channelCards:
                showAllChannels();
                return;

            case shelfKeys.vodWatchAgainCards:
                showAllWatchAgain();
                return;

            case shelfKeys.vodContinueWatchingCards:
                showAllContinueWatching();
                return;

            case shelfKeys.mostPopularCards:
                showAllMostPopular();
                return;

            case shelfKeys.newShowsCards:
                showAllNewShows();
                return;

            case shelfKeys.newMoviesCards:
                showAllNewMovies();
                return;

            case shelfKeys.recommendedShowsCards:
                showAllRecommendedShows();
                return;

            case shelfKeys.recommendedMoviesCards:
                showAllRecommendedMovies();
                return;

            case shelfKeys.newReleasesCards:
                showAllNewReleasesCards();
                return;
        }

        if (val.lineId.startsWith(shelfKeys.genresCards)) {
            showAllVodInternalGenres();
            return;
        }
    };
    const showAllMostPopular = (): void => {
        router.push(`${linkItems.showAllMostPopular.routerPath}&filter=${getFilterVod.value}`);
    };
    const showAllWatchAgain = (): void => {
        router.push(`${linkItems.showAllWatchAgain.routerPath}`);
    };
    const showAllContinueWatching = (): void => {
        router.push(`${linkItems.showAllContinueWatchingVod.routerPath}&filter=${getFilterVod.value}`);
    };
    const showAllChannels = (): void => {
        router.push(`${linkItems.showAllChannels.routerPath}`);
    };
    const showAllNewShows = (): void => {
        router.push(`${linkItems.showAllNewShows.routerPath}&filter=${getFilterVod.value}`);
    };
    const showAllNewMovies = (): void => {
        router.push(`${linkItems.showAllNewMovies.routerPath}&filter=${getFilterVod.value}`);
    };
    const showAllRecommendedShows = (): void => {
        router.push(`${linkItems.showAllRecommendedShows.routerPath}&filter=${getFilterVod.value}`);
    };
    const showAllRecommendedMovies = (): void => {
        router.push(`${linkItems.showAllRecommendedMovies.routerPath}&filter=${getFilterVod.value}`);
    };
    const showAllNewReleasesCards = (): void => {
        router.push(`${linkItems.showAllRecentlyOnDemand.routerPath}&filter=${getFilterVod.value}`);
    };
    const showAllVodInternalGenres = (): void => {
        router.push(`${linkItems.showAllForGenre.routerPath}?filter=${getFilterVod.value}`);
    };
    const focusElement = (): void => {
        if (isHavSelectedIdProgram.value) {
            focusElementLine();
        } else {
            focusToHeader();
        }

        setDisableRouteChange(false);
    };
    const focusMostPopular = (): void => {
        if (MostPopularSlide.value) {
            MostPopularSlide.value.focusMostPopular();
        }
    };

    const itemsSelected = (val: IEventSelected): void => {
        if (val.lineId === shelfKeys.channelCards) {
            channelSelected(val.item as GenreCardData);
        } else if (val.lineId === shelfKeys.tabsGenresCards) {
            gotoTabGenresDetailsPage(val.item as TabsGenresCardData);
        } else if (val.lineId === shelfKeys.filtersCards) {
            filterPage(val.item as FiltersVodCardData);
        } else if (PlayItem.isSeries((val.item as OnDemandCardData).getPlayItem())) {
            gotoSeriesDetailsPage(val.item as OnDemandCardData);
        } else {
            gotoMovieDetailsPage(val.item as OnDemandCardData);
        }
    };
    const filterPage = async (item: FiltersVodCardData): Promise<void> => {
        const filterQuery = route.query.filter;
        if (filterQuery === item.type) {
            return;
        }
        setFilter(item.type);
        clearLinesVodStoreByFilter();
        router.replace({ path: linkItems.onDemand.routerPath, query: { "filter": `${item.type}`}}).catch(()=>{});
    };
    const channelSelected = (genre: GenreCardData) => {
        if (genre.getIsPremium()) {
            router.push(`${linkItems.subgenresPremium.routerPath}/${genre.getId()}`);
            return;
        }
        router.push(`${linkItems.subgenres.routerPath}/${genre.getId()}`);
    };
    const gotoTabGenresDetailsPage = (item: TabsGenresCardData): void => {
        router.push(`${linkItems.detailGenre.routerPath}/${item.getGenreId()}`);
    };
    const gotoMovieDetailsPage = (item: OnDemandCardData): void => {
        try {
            const playItem = item.getPlayItem();
            if (!playItem) {
                throw new Error("playItem is null:" + JSON.stringify(item));
            }
            setViewedDetailItems(playItem);
            router.push(`${linkItems.detailsVodMovies.routerPath}/${item.getId()}`);
        } catch (error) {
            Logger.captureError(error as Error);
            setTooltipeMessage(t(Strings.DETAILS_TOOLTIP_ERROR_OPENING_DETAILS_PAGE).toString());
            setTooltipeShowing({visible: true, showingTime: 5000});
        }
    };
    const gotoSeriesDetailsPage = (item: OnDemandCardData): void => {
        try {
            const playItem = item.getPlayItem();
            if (!playItem) {
                throw new Error("playItem is null:" + JSON.stringify(item));
            }
            setViewedDetailItems(playItem);
            router.push(`${linkItems.detailsVodSeries.routerPath}/${item.getId()}`);
        } catch (error) {
            Logger.captureError(error as Error);
            setTooltipeMessage(t(Strings.DETAILS_TOOLTIP_ERROR_OPENING_DETAILS_PAGE).toString());
            setTooltipeShowing({visible: true, showingTime: 5000});
        }
    };

    const focusFirstRow = (): void => {
        if (linesVod.value) {
            linesVod.value.focusFirstRow();
        }
    };

    const leaveLinesVod = (): void => {
        if(isEmptyMostPopular.value) {
            focusToHeader();
        } else {
            focusMostPopular();
        }
    };

    const leaveHeader = (): void => {
        if(isEmptyMostPopular.value) {
            focusFirstRow();
        } else {
            focusMostPopular();
        }
    };

    const handlerReady = (): void => {
        nextTick(()=>{
            setIsHaveFocusedElement();
            if (!isTypeModeEnabledPin.value) {
                focusElement();
            }
        });

        if (traceOnDemandScreen) {
            try {
                traceOnDemandScreen.stop();
                Logger.captureLog(`ON_DEMAND_SCREEN stop ${traceOnDemandScreen.durationUs / 1000000} sec`);
            } catch (e){
                Logger.captureLog(e);
            }
        }
    };
    onMounted(() => {
        nextTick((): void => {
            $eventBus.on("key-up-rows", leaveLinesVod);
            $eventBus.on("leave-header", leaveHeader);

            $analytics.createScreenEvent(ScreenName.VOD_PAGE, ScreenName.VOD_PAGE);
        });
    });

    onUnmounted(() => {
        $eventBus.off("key-up-rows", leaveLinesVod);
        $eventBus.off("leave-header", leaveHeader);
    });


    onBeforeRouteUpdate((_to: RouteLocationNormalizedGeneric, _from: RouteLocationNormalizedLoadedGeneric, next: NavigationGuardNext) => {
        next();

        nextTick(() => {
            setIsLoadingPageIndicator(false);
        });
    });

    onBeforeRouteLeave((_to: RouteLocationNormalizedGeneric, _from: RouteLocationNormalizedLoadedGeneric, next: NavigationGuardNext) => {
        if (disableRouteChange.value) { // back after add see ondemand
            return;
        }

        next();
    });
    watch(isReadyAll, (value: boolean) => {
        if (value){
            handlerReady();
        }
    });
</script>

<style lang="scss">
    @import url("styles.scss");
</style>